// src/pages/CRM/SFA/SFADashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, Avatar, Divider, CircularProgress, Alert,
  Stack
} from '@mui/material';
import {
  TrendingUp as OpportunityIcon,
  Inventory as ProductIcon,
  Description as QuoteIcon,
  Assessment as ForecastIcon,
  Flag as TargetIcon,
  AttachMoney as MoneyIcon,
  ShowChart as PipelineIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { hasPermission } from '../../../utils/permissionUtils';
import { formatCurrency, formatPercent } from './sfaEnums';
import { opportunityService, productService, quoteService, forecastService } from './sfaService';
import axiosInstance from '../../../utils/axiosInstance';

const SFADashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'SFA Dashboard | KlonIT Web Application';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadDashboard();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      
      // Use Promise.allSettled instead of Promise.all to handle individual failures gracefully
      // This ensures that if one API call fails (e.g., forecast returns 500), 
      // the other successful responses are still processed
      const [oppsRes, productsRes, quotesRes, forecastRes, teamsRes] = await Promise.allSettled([
        opportunityService.getAll({ ...params, per_page: 5 }),
        productService.getAll({ ...params, status: 'ACTIVE', per_page: 5 }),
        quoteService.getAll({ ...params, per_page: 5 }),
        forecastService.getSummary(params),
        axiosInstance.get('/api/crm/teams', { params: { limit: 100 } })
      ]);

      // Log any failed requests for debugging
      if (oppsRes.status === 'rejected') {
        console.error('Opportunities fetch failed:', oppsRes.reason);
      }
      if (productsRes.status === 'rejected') {
        console.error('Products fetch failed:', productsRes.reason);
      }
      if (quotesRes.status === 'rejected') {
        console.error('Quotes fetch failed:', quotesRes.reason);
      }
      if (forecastRes.status === 'rejected') {
        console.error('Forecast fetch failed:', forecastRes.reason);
      }
      if (teamsRes.status === 'rejected') {
        console.error('Teams fetch failed:', teamsRes.reason);
      }

      // Process teams data
      let teamsData = [];
      if (teamsRes.status === 'fulfilled') {
        teamsData = teamsRes.value.data?.teams || teamsRes.value.data?.data || teamsRes.value.data || [];
        if (!Array.isArray(teamsData)) teamsData = [];
      }

      setDashboardData({
        opportunities: {
          total: oppsRes.status === 'fulfilled' 
            ? (oppsRes.value.pagination?.total || 0) 
            : 0,
          open: oppsRes.status === 'fulfilled' 
            ? (oppsRes.value.data?.filter(o => !['CLOSED_WON', 'CLOSED_LOST'].includes(o.stage)).length || 0) 
            : 0,
          recent: oppsRes.status === 'fulfilled' 
            ? (oppsRes.value.data || []) 
            : []
        },
        products: {
          total: productsRes.status === 'fulfilled' 
            ? (productsRes.value.pagination?.total || 0) 
            : 0,
          active: productsRes.status === 'fulfilled' 
            ? (productsRes.value.data?.filter(p => p.status === 'ACTIVE').length || 0) 
            : 0
        },
        quotes: {
          total: quotesRes.status === 'fulfilled' 
            ? (quotesRes.value.pagination?.total || 0) 
            : 0,
          pending: quotesRes.status === 'fulfilled' 
            ? (quotesRes.value.data?.filter(q => q.status === 'SENT').length || 0) 
            : 0,
          recent: quotesRes.status === 'fulfilled' 
            ? (quotesRes.value.data || []) 
            : []
        },
        forecast: forecastRes.status === 'fulfilled' 
          ? (forecastRes.value.data?.summary || {}) 
          : {},
        teams: {
          total: teamsData.length,
          active: teamsData.filter(t => t.is_active).length
        }
      });

      // Show warning if some data couldn't be loaded (optional - can be removed)
      const failedCount = [oppsRes, productsRes, quotesRes, forecastRes, teamsRes]
        .filter(r => r.status === 'rejected').length;
      
      if (failedCount > 0) {
        console.warn(`${failedCount} API call(s) failed. Some dashboard data may be incomplete.`);
      }

    } catch (err) {
      setError('Failed to load SFA dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view the SFA module.
      </Alert>
    );
  }

  const cards = [
    {
      title: 'Opportunities',
      description: 'Track sales opportunities through your pipeline from prospecting to close.',
      icon: <OpportunityIcon sx={{ fontSize: 60, color: '#2196f3' }} />,
      count: dashboardData?.opportunities?.total || 0,
      subtitle: `${dashboardData?.opportunities?.open || 0} open deals`,
      link: `/chatbot/${chatbotId}/crm/opportunities`,
      color: '#2196f3',
      actions: [
        { label: 'Pipeline View', link: `/chatbot/${chatbotId}/crm/opportunities/pipeline`, icon: <PipelineIcon /> }
      ]
    },
    {
      title: 'Products',
      description: 'Manage your product catalog with pricing, categories, and inventory details.',
      icon: <ProductIcon sx={{ fontSize: 60, color: '#9c27b0' }} />,
      count: dashboardData?.products?.total || 0,
      subtitle: `${dashboardData?.products?.active || 0} active products`,
      link: `/chatbot/${chatbotId}/crm/products`,
      color: '#9c27b0'
    },
    {
      title: 'Quotes',
      description: 'Create professional quotes with line items, discounts, and terms.',
      icon: <QuoteIcon sx={{ fontSize: 60, color: '#ff9800' }} />,
      count: dashboardData?.quotes?.total || 0,
      subtitle: `${dashboardData?.quotes?.pending || 0} pending approval`,
      link: `/chatbot/${chatbotId}/crm/quotes`,
      color: '#ff9800'
    },
    {
      title: 'Forecast',
      description: 'Revenue predictions and pipeline analysis for accurate forecasting.',
      icon: <ForecastIcon sx={{ fontSize: 60, color: '#00bcd4' }} />,
      count: formatCurrency(dashboardData?.forecast?.total_weighted || 0),
      subtitle: 'Weighted forecast',
      link: `/chatbot/${chatbotId}/crm/forecast`,
      color: '#00bcd4'
    },
    {
      title: 'Sales Targets',
      description: 'Set and track sales quotas for individuals, teams, and the organization.',
      icon: <TargetIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
      count: formatCurrency(dashboardData?.forecast?.target || 0),
      subtitle: 'Current period target',
      link: `/chatbot/${chatbotId}/crm/targets`,
      color: '#4caf50'
    },
    {
      title: 'Sales Teams',
      description: 'Manage sales teams, assign team owners, and organize your sales force.',
      icon: <GroupsIcon sx={{ fontSize: 60, color: '#e91e63' }} />,
      count: dashboardData?.teams?.total || 0,
      subtitle: `${dashboardData?.teams?.active || 0} active teams`,
      link: `/chatbot/${chatbotId}/crm/teams`,
      color: '#e91e63'
    }
  ];

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <PipelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Sales Force Automation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage opportunities, quotes, and sales performance
          </Typography>
        </Box>
      </Box>

      {/* Main Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: '0 auto 16px'
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="h3" color={card.color} fontWeight={700} sx={{ mb: 2 }}>
                  {card.count}
                </Typography>
                {card.subtitle && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {card.subtitle}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Manage
                </Button>
                {card.actions?.map((action, idx) => (
                  <Button
                    key={idx}
                    variant="outlined"
                    size="small"
                    startIcon={action.icon}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(action.link);
                    }}
                    sx={{ borderColor: card.color, color: card.color }}
                  >
                    {action.label}
                  </Button>
                ))}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pipeline Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Pipeline Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Total Pipeline</Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(dashboardData?.forecast?.total_pipeline || 0)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Weighted Forecast</Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {formatCurrency(dashboardData?.forecast?.total_weighted || 0)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Closed Won</Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {formatCurrency(dashboardData?.forecast?.closed || 0)}
                </Typography>
              </Box>

              {dashboardData?.forecast?.target > 0 && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Target Achievement</Typography>
                    <Chip 
                      label={formatPercent(dashboardData?.forecast?.achievement_percent || 0)}
                      color={
                        (dashboardData?.forecast?.achievement_percent || 0) >= 100 
                          ? 'success' 
                          : (dashboardData?.forecast?.achievement_percent || 0) >= 75 
                          ? 'warning' 
                          : 'default'
                      }
                    />
                  </Box>
                </>
              )}
            </Stack>

            {canModify && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate(`/chatbot/${chatbotId}/crm/forecast`)}
                >
                  View Full Forecast
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Recent Opportunities
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {dashboardData?.opportunities?.recent?.length > 0 ? (
              <Stack spacing={2}>
                {dashboardData.opportunities.recent.slice(0, 5).map((opp, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/${opp.id}`)}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {opp.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opp.account?.name || 'No Account'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {formatCurrency(opp.amount, opp.currency)}
                      </Typography>
                      <Chip
                        label={`${opp.probability}%`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No recent opportunities
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      {canModify && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities/new`)}
            >
              New Opportunity
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/quotes/new`)}
            >
              New Quote
            </Button>
            <Button
              variant="outlined"
              startIcon={<ProductIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/products`)}
            >
              Manage Products
            </Button>
            <Button
              variant="outlined"
              startIcon={<TargetIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/targets`)}
            >
              Set Targets
            </Button>
            <Button
              variant="outlined"
              startIcon={<GroupsIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/teams`)}
            >
              Manage Teams
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default SFADashboard;